import React, { Suspense, lazy, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import games from '../games/manifest';
import { getEnabledGames } from '../utils/randomPool';
import { useTeamMode } from '../utils/useTeamMode';
import { useRandomScore } from '../utils/useRandomScore';
import TeamSetup from '../components/TeamSetup';
import TeamScoreboard, { CurrentTeamIndicator } from '../components/TeamScoreboard';
import TeamGameWrapper from '../components/TeamGameWrapper';
import { UsersIcon, XIcon, RotateIcon } from '../utils/icons';

function DiceIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="3" stroke="#E8C96E" strokeWidth="2"/>
      <circle cx="8.5" cy="8.5" r="1.5" fill="#E8C96E"/>
      <circle cx="15.5" cy="8.5" r="1.5" fill="#E8C96E"/>
      <circle cx="12" cy="12" r="1.5" fill="#E8C96E"/>
      <circle cx="8.5" cy="15.5" r="1.5" fill="#E8C96E"/>
      <circle cx="15.5" cy="15.5" r="1.5" fill="#E8C96E"/>
    </svg>
  );
}

function pickRandom(exclude) {
  const enabled = getEnabledGames(games);
  const pool = enabled.filter((g) => g.id !== exclude);
  const candidates = pool.length > 0 ? pool : enabled;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

export default function RandomGame() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [gameEntry, setGameEntry] = useState(() => pickRandom());
  const [key, setKey] = useState(0);
  const [showTeamSetup, setShowTeamSetup] = useState(false);

  const teamMode = useTeamMode();
  const {
    isTeamMode,
    teams,
    currentTeam,
    teamScores,
    gameStarted,
    setupTeams,
    startTeamGame,
    addPoints,
    nextTeam,
    resetGame,
    exitTeamMode
  } = teamMode;

  const randomScore = useRandomScore();
  const { score: cumulativeScore, addPoints: addRandomPoints, resetScore: resetRandomScore } = randomScore;

  const nextRandom = useCallback(() => {
    setGameEntry((prev) => pickRandom(prev.id));
    setKey((k) => k + 1);
  }, []);

  // Team-aware round end handler
  const handleRoundEnd = useCallback(() => {
    // Don't handle points here - TeamGameWrapper will handle team scoring
    // based on game history changes
    if (!isTeamMode) {
      // For non-team mode, we'll track points separately
      // The games already track their own points via useStreakPoints
    }
    // Always advance to next random game
    nextRandom();
  }, [isTeamMode, nextRandom]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) nextRandom();
      if (e.key === 't' && !e.ctrlKey && !e.metaKey && !isTeamMode) {
        setShowTeamSetup(true);
      }
    };
    window.addEventListener('keyup', handler);
    return () => window.removeEventListener('keyup', handler);
  }, [nextRandom, isTeamMode]);

  const handleSetupTeams = (teamNames) => {
    setupTeams(teamNames);
    startTeamGame();
  };

  const GameComponent = lazy(gameEntry.loader);

  return (
    <div className="game-wrapper">
      <header className="game-wrapper-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          &#8592; {t('game.back')}
        </button>
        <h2>{t(gameEntry.titleKey)}</h2>
        <div className="header-actions">
          {!isTeamMode ? (
            <button 
              className="btn-team-mode tooltip-wrap" 
              onClick={() => setShowTeamSetup(true)}
              title={t('teamMode.setup.title', 'Team Mode')}
            >
              <UsersIcon />
              <span className="tooltip">{t('teamMode.enable', 'Enable Team Mode')}</span>
            </button>
          ) : (
            <div className="team-header-controls">
              <button 
                className="btn-team-reset tooltip-wrap" 
                onClick={resetGame}
                title={t('teamMode.reset', 'Reset Scores')}
              >
                <RotateIcon />
                <span className="tooltip">{t('teamMode.reset', 'Reset Scores')}</span>
              </button>
              <button 
                className="btn-team-exit tooltip-wrap" 
                onClick={exitTeamMode}
                title={t('teamMode.exit', 'Exit Team Mode')}
              >
                <XIcon />
                <span className="tooltip">{t('teamMode.exit', 'Exit Team Mode')}</span>
              </button>
            </div>
          )}
          <button className="btn-shuffle tooltip-wrap" onClick={nextRandom}>
            <DiceIcon />
            <span className="tooltip">{t('random.next')}</span>
          </button>
        </div>
      </header>

      {isTeamMode && gameStarted ? (
        <div className="team-game-ui">
          <div className="team-current-info">
            <CurrentTeamIndicator team={currentTeam} />
            <TeamScoreboard 
              teams={teams} 
              teamScores={teamScores} 
              currentTeamId={currentTeam?.id}
              compact={true}
            />
          </div>
        </div>
      ) : (
        <div className="random-score-ui">
          <div className="random-score-display">
            <div className="score-label">{t('random.totalScore', 'Total Score')}</div>
            <div className="score-value">{cumulativeScore}</div>
            <button 
              className="btn-score-reset tooltip-wrap" 
              onClick={resetRandomScore}
              title={t('random.resetScore', 'Reset Score')}
            >
              <RotateIcon />
              <span className="tooltip">{t('random.resetScore', 'Reset Score')}</span>
            </button>
          </div>
        </div>
      )}

      <div className="game-wrapper-content">
        <Suspense fallback={<div className="game-loading">{t('game.loading')}</div>}>
          <TeamGameWrapper
            isTeamMode={isTeamMode && gameStarted}
            onWrongAnswer={nextTeam}
            onCorrectAnswer={(pointsEarned) => {
              if (isTeamMode && gameStarted) {
                // Award points to the current team
                addPoints(pointsEarned || 1);
              } else {
                // Award points to cumulative score when not in team mode
                addRandomPoints(pointsEarned || 1);
              }
            }}
          >
            <GameComponent 
              key={key} 
              onRoundEnd={handleRoundEnd} 
            />
          </TeamGameWrapper>
        </Suspense>
      </div>

      <TeamSetup 
        isOpen={showTeamSetup} 
        onClose={() => setShowTeamSetup(false)}
        onSetupTeams={handleSetupTeams}
      />
    </div>
  );
}
